import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import SystemButton from "../SystemButton/SystemButton";
import { CopyButton } from "./CopyButton";

type Props = {
	label: string;
	value: string;
	/** Shows a copy-to-clipboard button inside the chip. */
	copyable?: boolean;
	/**
	 * Renders the value as dots until the reader reveals it with the added eye button. For a value
	 * that is a credential rather than an identifier — a password, a token, an API key — so it is not
	 * left sitting on screen behind a screen share. Copy still works while hidden: copying a secret
	 * does not require reading it.
	 */
	secret?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const CHIP_SX: SxProps<Theme> = {
	display: "inline-flex",
	alignItems: "center",
	gap: 0.5,
	alignSelf: "flex-start",
	maxWidth: "100%",
	pl: 1.25,
	pr: 0.5,
	py: 0.5,
	borderRadius: 1.5,
	backgroundColor: NEUTRAL[100],
	border: `1px solid ${NEUTRAL[200]}`,
};

/** How many dots stand in for a hidden secret. Fixed, so the mask never leaks the value's length. */
const MASK = "•".repeat(12);

/** Matches `CopyButton`'s `dense` box, so the two sit as one pair of controls inside the chip. */
const TOGGLE_BOX_SIZE = "24px";
const TOGGLE_GLYPH_SIZE = "1rem";

/** Read-only system value (ID, slug, UUID): muted uppercase label + monospace code chip, optional copy button. */
export const ReadOnlyMetadataField: FC<Props> = ({ label, value, copyable = false, secret = false, dataTestId, sx }) => {
	const { t } = useTranslation();
	const [revealed, setRevealed] = useState(false);
	const toggleLabel = revealed ? t("system.action.hide") : t("system.action.reveal");

	return (
		// `minWidth: 0` is load-bearing, not defensive: the value below is `whiteSpace: nowrap`, so this
		// box's min-content width is the WHOLE string. As a flex item that means it refuses to shrink,
		// overflows its column and paints over whatever sits beside it — and the chip's `maxWidth: 100%`
		// and ellipsis never engage, because they measure against a box that already grew to fit.
		<Box data-testid={dataTestId} sx={[{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			<Typography
				component="span"
				sx={{ fontWeight: 600, fontSize: FONT_SIZE.LABEL_XS, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.2, color: INK.MUTED }}
			>
				{label}
			</Typography>
			<Box sx={CHIP_SX}>
				<Typography
					component="span"
					sx={{
						fontFamily: "monospace",
						fontSize: FONT_SIZE.SECONDARY,
						lineHeight: 1.4,
						color: INK.SECONDARY,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{secret && !revealed ? MASK : value}
				</Typography>
				{secret && (
					<SystemButton
						buttonVariant="borderless"
						iconButton
						onClick={() => setRevealed((previous) => !previous)}
						tooltip={toggleLabel}
						aria-label={toggleLabel}
						data-testid={dataTestId ? `${dataTestId}-reveal` : undefined}
						sx={{ minWidth: TOGGLE_BOX_SIZE, width: TOGGLE_BOX_SIZE, height: TOGGLE_BOX_SIZE, color: INK.MUTED }}
					>
						{revealed ? <VisibilityOffOutlined sx={{ fontSize: TOGGLE_GLYPH_SIZE }} /> : <VisibilityOutlined sx={{ fontSize: TOGGLE_GLYPH_SIZE }} />}
					</SystemButton>
				)}
				{copyable && <CopyButton value={value} size="dense" iconFontSize={TOGGLE_GLYPH_SIZE} dataTestId={dataTestId ? `${dataTestId}-copy` : undefined} />}
			</Box>
		</Box>
	);
};
